import React from "react";
import {
    ArrowRight,
    Calendar,
    Layers,
} from "lucide-react";
import useAuth from "../contexts/AuthContext";
// ─── Sub-components ───────────────────────────────────────────────────────────

interface SectionHeaderProps {
    title: string;
    subtitle?: string;
}

const SectionHeader = ({ title, subtitle }: SectionHeaderProps) => (
    <div className="mb-6">
        <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
);

interface EmptyStateProps {
    message: string;
}

const EmptyState = ({ message }: EmptyStateProps) => (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-400 border border-dashed border-slate-200 rounded-xl bg-slate-50">
        <Layers className="w-7 h-7" />
        <p className="text-sm">{message}</p>
    </div>
);

const SkeletonLoader = () => (
    <div className="animate-pulse space-y-3 py-4">
        <div className="h-4 bg-slate-200 rounded w-2/3" />
        <div className="h-12 bg-slate-100 rounded" />
        <div className="h-12 bg-slate-100 rounded" />
    </div>
);

// ─── Panel: Admissions ────────────────────────────────────────────────────────

export interface AdmissionItem {
    _id: string;
    title?: string;
    name?: string;
    applicationDates?: {
        startDate?: string;
        endDate?: string;
    };
    applicationLink?: string;
}

interface ApplyPanelProps {
    admissions: AdmissionItem[];
    isLoading: boolean;
    registrationClosed?: boolean;
    page?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
    showArchive?: boolean;
    onShowArchiveChange?: (show: boolean) => void;
    title?: string;
    subtitle?: string;
}

export const ApplyPanel = ({
    admissions,
    isLoading,
    registrationClosed,
    page,
    totalPages,
    onPageChange,
    showArchive,
    onShowArchiveChange,
    title = "Current admissions",
    subtitle = "Open recruitment cycles for the current academic year."
}: ApplyPanelProps) => {
    const { isStudentAdmin, isSuperAdmin } = useAuth();
    const isAdmin = isStudentAdmin || isSuperAdmin;
    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                <SectionHeader
                    title={title}
                    subtitle={subtitle}
                />
                {onShowArchiveChange && (
                    <label className="inline-flex items-center cursor-pointer gap-2 bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 transition-colors self-start sm:self-auto">
                        <input
                            type="checkbox"
                            checked={showArchive}
                            onChange={(e) => onShowArchiveChange(e.target.checked)}
                            className="rounded text-slate-900 focus:ring-slate-900 border-slate-350 w-4 h-4 cursor-pointer"
                        />
                        <span className="text-xs font-semibold text-slate-700">Show Archive</span>
                    </label>
                )}
            </div>
            {isLoading ? (
                <SkeletonLoader />
            ) : admissions.length > 0 ? (
                <div className="space-y-3">
                    {admissions.map((adm) => {
                        const now = new Date();
                        const startDate = adm.applicationDates?.startDate ? new Date(adm.applicationDates.startDate) : null;
                        const endDate = adm.applicationDates?.endDate ? new Date(adm.applicationDates.endDate) : null;

                        const isUpcoming = startDate ? now < startDate : false;
                        const isEnded = endDate ? now > endDate : false;
                        const isClosed = registrationClosed || isEnded;
                        const canApply = (!isUpcoming || isAdmin) && !isClosed;

                        let buttonText = "Go to Applications";
                        if (isUpcoming && !isAdmin) {
                            buttonText = startDate
                                ? `Starts on ${startDate.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`
                                : "Opening Soon";
                        } else if (isUpcoming && isAdmin) {
                            buttonText = "Go to Applications (Admin Test)";
                        } else if (isClosed) {
                            buttonText = "Registration closed";
                        }

                        return (
                            <div
                                key={adm._id}
                                className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 hover:border-slate-300 transition-colors"
                            >
                                <div className="flex flex-col gap-2">
                                    <h4 className="text-sm font-semibold text-slate-900 leading-snug">{adm.title || adm.name}</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {adm.applicationDates?.startDate && adm.applicationDates?.endDate && (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-full px-3 py-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(adm.applicationDates.startDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                                                {" – "}
                                                {new Date(adm.applicationDates.endDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                            </span>
                                        )}
                                        {isUpcoming && (
                                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                                                Opening Soon {isAdmin ? "(Admin Test Mode)" : ""}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <a
                                    href={adm.applicationLink}
                                    className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors flex-shrink-0 ${!canApply
                                        ? "bg-slate-100 text-slate-400 cursor-not-allowed pointer-events-none"
                                        : "bg-slate-900 text-white hover:bg-slate-700"
                                        }`}
                                >
                                    {buttonText}
                                    {canApply && <ArrowRight className="w-3.5 h-3.5" />}
                                </a>
                            </div>
                        );
                    })}

                    {totalPages && totalPages > 1 && page && onPageChange && (
                        <div className="flex items-center justify-between border-t border-slate-100 pt-6 mt-6">
                            <p className="text-xs text-slate-500 font-medium">
                                Showing page <span className="font-semibold text-slate-800">{page}</span> of{" "}
                                <span className="font-semibold text-slate-800">{totalPages}</span>
                            </p>
                            <div className="flex gap-2">
                                <button
                                    disabled={page === 1}
                                    onClick={() => onPageChange(page - 1)}
                                    className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                                >
                                    Previous
                                </button>
                                <button
                                    disabled={page === totalPages}
                                    onClick={() => onPageChange(page + 1)}
                                    className="px-3 py-1.5 text-xs font-semibold bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 text-white disabled:opacity-50 disabled:pointer-events-none transition-colors"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <EmptyState message="No admissions or recruitments are currently available." />
            )}
        </div>
    );
};

// ─── Panel: Notices ───────────────────────────────────────────────────────────

export interface NoticeItem {
    link?: string;
    title?: string;
    message?: string;
    date?: string;
}

interface NotifyPanelProps {
    notices: NoticeItem[];
    isLoading: boolean;
    page?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
    showArchive?: boolean;
    onShowArchiveChange?: (show: boolean) => void;
}

export const NotifyPanel = ({
    notices,
    isLoading,
    page,
    totalPages,
    onPageChange,
    showArchive,
    onShowArchiveChange
}: NotifyPanelProps) => (
    <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
            <SectionHeader
                title="Latest notices"
                subtitle="Official communications from the admissions office."
            />
            {onShowArchiveChange && (
                <label className="inline-flex items-center cursor-pointer gap-2 bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 transition-colors self-start sm:self-auto">
                    <input
                        type="checkbox"
                        checked={showArchive}
                        onChange={(e) => onShowArchiveChange(e.target.checked)}
                        className="rounded text-slate-900 focus:ring-slate-900 border-slate-350 w-4 h-4 cursor-pointer"
                    />
                    <span className="text-xs font-semibold text-slate-700">Show Archive</span>
                </label>
            )}
        </div>
        {isLoading ? (
            <SkeletonLoader />
        ) : notices.length > 0 ? (
            <div className="space-y-4">
                <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                    {notices.map((notice, i) => (
                        <a
                            key={i}
                            href={notice.link || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-3 px-5 py-4 bg-white hover:bg-slate-50 transition-colors cursor-pointer block"
                        >
                            {/* Decorative blue dot */}
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />

                            <div className="flex-1 min-w-0">
                                {/* Notice Content */}
                                <p className="text-sm text-slate-800 leading-relaxed">
                                    {notice.title || notice.message}
                                </p>

                                {/* Notice Date */}
                                {notice.date && (
                                    <p className="text-xs text-slate-400 mt-1">
                                        {new Date(notice.date).toLocaleDateString("en-IN", {
                                            day: "numeric",
                                            month: "long",
                                            year: "numeric"
                                        })}
                                    </p>
                                )}
                            </div>
                        </a>
                    ))}
                </div>

                {totalPages && totalPages > 1 && page && onPageChange && (
                    <div className="flex items-center justify-between border-t border-slate-100 pt-6 mt-2">
                        <p className="text-xs text-slate-500 font-medium">
                            Showing page <span className="font-semibold text-slate-800">{page}</span> of{" "}
                            <span className="font-semibold text-slate-800">{totalPages}</span>
                        </p>
                        <div className="flex gap-2">
                            <button
                                disabled={page === 1}
                                onClick={() => onPageChange(page - 1)}
                                className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                            >
                                Previous
                            </button>
                            <button
                                disabled={page === totalPages}
                                onClick={() => onPageChange(page + 1)}
                                className="px-3 py-1.5 text-xs font-semibold bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 text-white disabled:opacity-50 disabled:pointer-events-none transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        ) : (
            <EmptyState message="No new notices at this time." />
        )}
    </div>
);
